import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
  Breadcrumbs,
  Link,
  Fade,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { standardFieldLabelSx } from '../../components/common/formLabelStyles';
import { getConfigCrudDefinition } from './configCrudDefinitions';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import { HCP_MODEL_SEED, DRUG_MODEL_SEED } from './configurationSeeds';

const CLEANSER_TYPE_OPTIONS = ['Out of the box Cleanser', 'Custom cleanser'];

const OUT_OF_BOX_CLEANSER_OPTIONS = [
  'Fullname Cleanser',
  'String Cleanser',
  'Phone Cleanser',
  'Address Cleanser',
  'Website Cleanser',
  'Email Cleanser',
];

const cardSx = {
  minHeight: 300,
  // p: 4,
  borderRadius: 3,
  border: 'none',
  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
  backgroundColor: '#fff',
};

const pageHeaderSx = {
  px: 2.25,
  py: 1.5,
  borderBottom: '1px solid #E5E7EB',
  borderLeft: '4px solid #1E88E5',
  display: 'flex',
  backgroundColor: '#fbfdfe',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  mb: 3,
};

const inputFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 38,
    minHeight: 38,
    '& .MuiOutlinedInput-input': {
      padding: '10px 12px',
    },
  },
};

const backButtonSx = {
  color: '#111827',
  backgroundColor: '#f3f4f6',
  width: 36,
  height: 36,
  '&:hover': {
    backgroundColor: '#e5e7eb',
  },
};

const primaryButtonSx = {
  minWidth: 100,
  px: 3,
  py: 0.8,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 2,
  backgroundColor: '#2196f3',
  color: '#fff',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#1d88e5',
    boxShadow: 'none',
  },
};

const secondaryButtonSx = {
  minWidth: 100,
  px: 3,
  py: 0.8,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 2,
  color: '#374151',
  borderColor: '#d1d5db',
  backgroundColor: '#fff',
  '&:hover': {
    backgroundColor: '#f9fafb',
    borderColor: '#9ca3af',
  },
};

const CleanserWorkspacePage = ({ entityType: entityTypeProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { entityType: entityTypeParam, mode, itemKey } = useParams();
  const entityType = entityTypeProp || entityTypeParam;
  const definition = getConfigCrudDefinition(entityType, 'cleansers');

  const [localError, setLocalError] = useState('');
  const [form, setForm] = useState({
    attribute: '',
    displayName: '',
    description: '',
    active: true,
    cleanserType: '',
    cleanser: '',
  });

  const modelSeed = entityType === 'drug' ? DRUG_MODEL_SEED : HCP_MODEL_SEED;
  const attributeOptions = useMemo(() => {
    const opts = new Set(modelSeed.map((m) => m.displayName || m.name));
    if (form.attribute) opts.add(form.attribute);
    return Array.from(opts);
  }, [modelSeed, form.attribute]);

  const isEditMode = mode === 'edit';
  const backPath = `/configuration/${entityType}/cleansers`;

  useEffect(() => {
    if (!definition) return;

    const cached = localStorage.getItem(definition.storageKey);
    let rows = [];
    try {
      rows = cached ? JSON.parse(cached) : [];
    } catch {
      rows = [];
    }

    if (isEditMode) {
      const stateRow = location.state?.row;
      const currentKey = decodeURIComponent(itemKey || '');
      // Look for match by displayName or attribute as per definition.getRowKey
      const matchedRow =
        stateRow || rows.find((row) => (row.displayName || row.attribute) === currentKey);

      if (matchedRow) {
        setForm({
          attribute: matchedRow.attribute || '',
          displayName: matchedRow.displayName || '',
          description: matchedRow.description || '',
          active: matchedRow.active ?? true,
          cleanserType: matchedRow.cleanserType || '',
          cleanser: matchedRow.cleanser || '',
        });
      }
    }
  }, [definition, isEditMode, itemKey, location.state]);

  const onChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Reset cleanser if type changes
      if (field === 'cleanserType') {
        next.cleanser = '';
      }
      return next;
    });
  };

  const onSave = () => {
    if (!form.attribute || !form.displayName || !form.cleanserType || !form.cleanser) {
      setLocalError('Required fields are missing.');
      return;
    }

    const cached = localStorage.getItem(definition.storageKey);
    let rows = [];
    try {
      rows = cached ? JSON.parse(cached) : [];
    } catch {
      rows = [];
    }

    const nextRow = {
      ...form,
      lastModifiedDate: new Date().toLocaleDateString('en-GB'),
    };

    let nextRows;
    if (isEditMode) {
      const currentKey = decodeURIComponent(itemKey || '');
      nextRows = rows.map((row) =>
        (row.displayName || row.attribute) === currentKey ? { ...row, ...nextRow } : row
      );
    } else {
      nextRows = [...rows, nextRow];
    }

    localStorage.setItem(definition.storageKey, JSON.stringify(nextRows));
    localStorage.setItem(`${definition.storageKey}__version`, definition.storageVersion);
    navigate(backPath);
  };

  if (!definition) {
    return <Alert severity="error">Configuration definition not found.</Alert>;
  }

  return (
    <Box sx={{ px: 1, pb: 4, mt: 1 }}>
      {/* Top Breadcrumb Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <PageBreadcrumbs
          items={[
            // { label: 'Configuration', path: `/configuration/${entityType}/model` },
            { label: 'Cleansers', path: backPath },
            { label: isEditMode ? 'Edit Cleanser' : 'Add Cleanser' },
          ]}
        />
      </Box>

      <Fade in timeout={600}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: 'none',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
            backgroundColor: '#ffffff',
            overflow: 'hidden',
          }}
        >
          <Box sx={pageHeaderSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: '#eaf4ff',
                  color: '#1E88E5',
                  display: 'flex',
                }}
              >
                <AutoFixHighIcon fontSize="small" />
              </Box> */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: '#1E293B', mb: 0.25, lineHeight: 1.2 }}
                >
                  {isEditMode ? 'Edit Cleanser' : 'Create Cleanser'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                  {isEditMode
                    ? 'Update the details for this record.'
                    : 'Configure and save a new cleanser.'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: 4 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6} lg={4}>
                <Typography sx={standardFieldLabelSx}>
                  Attributes{' '}
                  <Box component="span" sx={{ color: '#d32f2f' }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  sx={inputFieldSx}
                  value={form.attribute}
                  onChange={(e) => onChange('attribute', e.target.value)}
                  placeholder="Select Attribute"
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    Select Attribute
                  </MenuItem>
                  {attributeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography sx={{ mt: 0.5, color: '#6b7280' }}>
                  The attribute this function will validate
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Typography sx={standardFieldLabelSx}>
                  Name{' '}
                  <Box component="span" sx={{ color: '#d32f2f' }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  sx={inputFieldSx}
                  value={form.displayName}
                  onChange={(e) => onChange('displayName', e.target.value)}
                  placeholder="Short Name"
                />
                <Typography sx={{ mt: 0.5, color: '#6b7280' }}>Name Displayed in MDM UI</Typography>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Typography sx={standardFieldLabelSx}>Description</Typography>
                <TextField
                  fullWidth
                  size="small"
                  sx={inputFieldSx}
                  value={form.description}
                  onChange={(e) => onChange('description', e.target.value)}
                  placeholder="Enter description"
                />
                <Typography sx={{ mt: 0.5, color: '#6b7280' }}>
                  Optional. Descriptive information about this attribute
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography sx={{ ...standardFieldLabelSx, mb: 0 }}>Active</Typography>
                  <Switch
                    checked={form.active}
                    onChange={(e) => onChange('active', e.target.checked)}
                    color="primary"
                    size="small"
                  />
                </Box>
                <Typography sx={{ color: '#6b7280' }}>
                  Turn off if you don't want this function applied to you data
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Typography sx={standardFieldLabelSx}>
                  Cleanser Type{' '}
                  <Box component="span" sx={{ color: '#d32f2f' }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  sx={inputFieldSx}
                  value={form.cleanserType}
                  onChange={(e) => onChange('cleanserType', e.target.value)}
                  placeholder="Select Cleanser Type"
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    Select Cleanser Type
                  </MenuItem>
                  {CLEANSER_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                {form.cleanserType && (
                  <>
                    <Typography sx={standardFieldLabelSx}>
                      Cleanser{' '}
                      <Box component="span" sx={{ color: '#d32f2f' }}>
                        *
                      </Box>
                    </Typography>
                    {form.cleanserType === 'Custom cleanser' ? (
                      <TextField
                        fullWidth
                        size="small"
                        sx={inputFieldSx}
                        value={form.cleanser}
                        onChange={(e) => onChange('cleanser', e.target.value)}
                        placeholder="Enter URL"
                      />
                    ) : (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        sx={inputFieldSx}
                        value={form.cleanser}
                        onChange={(e) => onChange('cleanser', e.target.value)}
                        placeholder="Select Cleanser"
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="" disabled>
                          Select Cleanser
                        </MenuItem>
                        {OUT_OF_BOX_CLEANSER_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </>
                )}
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 8 }}>
              <Button
                className="action-btn cancel-btn"
                variant="outlined"
                sx={secondaryButtonSx}
                onClick={() => navigate(backPath)}
              >
                Cancel
              </Button>
              <Button
                className="action-btn process-btn"
                variant="contained"
                sx={primaryButtonSx}
                onClick={onSave}
              >
                {isEditMode ? 'Update' : 'Save'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

CleanserWorkspacePage.propTypes = {
  entityType: PropTypes.oneOf(['hcp', 'drug']),
};

export default CleanserWorkspacePage;
